import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  AtSign, Bot, Building2, Check, CreditCard, Eye, EyeOff, Globe, Info, KeyRound, LayoutGrid, Lock, Mail, Shapes, UserRound,
} from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { FormField } from '@/components/FormField';
import { LogoUpload } from '@/components/LogoUpload';
import { IndustryPicker } from '@/components/industry/IndustryPicker';
import { useIndustryTemplates } from '@/components/industry/useIndustryTemplates';
import { industryGroup, industryLabel } from '@/components/industry/industry-link-meta';
import { ENTITY_META, ENTITY_ORDER, industryIcon } from '@/pages/industry-configs/industry-meta';
import { companiesService, CustomLimits } from '@/services/companies.service';
import { Blueprint, BlueprintPlan, BlueprintRegistry, blueprintsService } from '@/services/blueprints.service';
import { BlueprintPlanSummary } from '@/components/industry/BlueprintPlanSummary';
import { familyIcon } from '@/pages/blueprints/blueprint-meta';
import { LocationFields, LocationValue, resolvedLocation, useLocalizationOptions } from '@/components/location/LocationFields';
import { plansService, Plan } from '@/services/plans.service';
import { pricingService, QuoteResult } from '@/services/pricing.service';
import { ALL_MODULE_KEYS, DEFAULT_MODULE_KEYS, MODULE_REGISTRY } from '@/config/modules';

type CountKey = keyof CustomLimits;

const COUNT_FIELDS: { key: CountKey; label: string; help?: string }[] = [
  { key: 'whatsappAccounts',  label: 'WhatsApp accounts' },
  { key: 'facebookAccounts',  label: 'Facebook accounts' },
  { key: 'googleAdsAccounts', label: 'Google Ads accounts' },
  { key: 'tiktokAccounts',    label: 'TikTok accounts' },
  { key: 'emailAccounts',     label: 'Email accounts' },
  { key: 'users',             label: 'Users / seats' },
  { key: 'botFlows',          label: 'Bot flows' },
  { key: 'contacts',          label: 'Contacts',          help: 'Charged per block' },
  { key: 'messagesPerMonth',  label: 'Messages / month',  help: 'Charged per block' },
  { key: 'aiCreditsPerMonth', label: 'AI credits / month',help: 'Charged per block' },
];

const slugify = (s: string) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function Section({ icon, title, subtitle, children, delay = 0, raised }: {
  icon: ReactNode; title: string; subtitle: string; children: ReactNode; delay?: number;
  /** Lift above later sections — the fade-up transform gives each section its own
      stacking context, which otherwise buries a dropdown under the next card. */
  raised?: boolean;
}) {
  return (
    <section
      className={clsx('fade-up rounded-2xl border border-border bg-surface shadow-soft', raised && 'relative z-20')}
      style={{ animationDelay: `${delay}ms` }}
    >
      <header className="flex items-start gap-3 border-b border-border px-5 py-4">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">{icon}</span>
        <div className="min-w-0">
          <h3 className="font-display text-[15px] font-semibold tracking-tight text-text-primary">{title}</h3>
          <p className="mt-0.5 text-xs text-text-muted">{subtitle}</p>
        </div>
      </header>
      <div className="px-5 pb-1 pt-5">{children}</div>
    </section>
  );
}

function Switch({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={clsx(
        'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors',
        checked ? 'bg-primary' : 'bg-border-strong',
      )}
    >
      <span className={clsx('inline-block h-5 w-5 rounded-full bg-white shadow-soft transition-transform', checked ? 'translate-x-5' : 'translate-x-0.5')} />
    </button>
  );
}

export default function CompanyCreate() {
  const nav = useNavigate();
  const templates = useIndustryTemplates();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [status, setStatus] = useState<'active' | 'trial' | 'suspended'>('active');
  const [trialEndsAt, setTrialEndsAt] = useState('');
  const [modulesEdited, setModulesEdited] = useState(false);
  const [enabledModules, setEnabledModules] = useState<string[]>(DEFAULT_MODULE_KEYS);
  const [defaultBotMode, setDefaultBotMode] = useState<'off' | 'flow' | 'ai_chat'>('off');
  const [includedAiCredits, setIncludedAiCredits] = useState('');
  const [form, setForm] = useState({
    name: '', slug: '', domain: '', planId: '', industry: '',
    expiresAt: new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10),
    adminFirstName: '', adminLastName: '', adminEmail: '', adminPassword: '',
  });
  const [slugTouched, setSlugTouched] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | undefined>(undefined);
  const [usesCustomPricing, setUsesCustomPricing] = useState(false);
  const [counts, setCounts] = useState<CustomLimits>({});
  const [quote, setQuote] = useState<QuoteResult | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const debounceRef = useRef<number | null>(null);
  const [blueprints, setBlueprints] = useState<Blueprint[] | null>(null);
  const [registry, setRegistry] = useState<BlueprintRegistry | null>(null);
  const [family, setFamily] = useState<string>('');
  const [applyBlueprint, setApplyBlueprint] = useState(true);
  const [bpPlan, setBpPlan] = useState<BlueprintPlan | null>(null);
  const locOptions = useLocalizationOptions();
  const [location, setLocation] = useState<LocationValue>({ country: 'AE', defaultCurrency: '', timezone: '' });

  useEffect(() => {
    Promise.all([blueprintsService.list(), blueprintsService.registry()])
      .then(([b, r]) => { setBlueprints(b); setRegistry(r); })
      .catch(() => setBlueprints([]));
  }, []);
  const familyIndustries = useMemo(() => (blueprints ?? []).flatMap((b) => b.industries), [blueprints]);
  const chosenBlueprint = blueprints?.find((b) => b.familyKey === family) ?? null;
  // What this company gets from its blueprint, refreshed as the type changes.
  useEffect(() => {
    setBpPlan(null);
    if (!form.industry || !chosenBlueprint?.industries.includes(form.industry)) return;
    let cancelled = false;
    blueprintsService.preview(form.industry).then((p) => { if (!cancelled) setBpPlan(p); }).catch(() => { /* interceptor toasts */ });
    return () => { cancelled = true; };
  }, [form.industry, chosenBlueprint]);

  useEffect(() => { plansService.list().then((rows) => { const p = rows.filter(plan => plan.isActive); setPlans(p); if (p[0]) { setForm(f => ({ ...f, planId: p[0]._id })); setEnabledModules(p[0].includedModules ?? DEFAULT_MODULE_KEYS); } }).catch(() => { /* API interceptor displays the error. */ }); }, []);

  useEffect(() => {
    if (modulesEdited) return;
    const allowed = plans.find(plan => plan._id === form.planId)?.includedModules ?? DEFAULT_MODULE_KEYS;
    const recommended = applyBlueprint ? bpPlan?.launchDefaults?.recommendedModules : undefined;
    setEnabledModules(recommended ? allowed.filter(key => recommended.includes(key)) : allowed);
  }, [plans, form.planId, bpPlan, applyBlueprint, modulesEdited]);

  // Debounced quote refresh as the super-admin edits counts.
  useEffect(() => {
    if (!usesCustomPricing) { setQuote(null); return; }
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(async () => {
      try { setQuote(await pricingService.quote(counts)); } catch { /* ignore */ }
    }, 250);
    return () => { if (debounceRef.current) window.clearTimeout(debounceRef.current); };
  }, [counts, usesCustomPricing]);

  const set = (k: keyof typeof form, v: string) => setForm(s => ({ ...s, [k]: v }));
  const pickFamily = (f: string) => {
    setFamily(f);
    // A type from another family would contradict the card that's now selected.
    const bp = blueprints?.find((b) => b.familyKey === f);
    setForm((s) => ({ ...s, industry: bp ? (bp.industries.includes(s.industry) ? s.industry : bp.industries[0] ?? '') : familyIndustries.includes(s.industry) ? '' : s.industry }));
  };
  const setName = (v: string) => setForm(s => ({ ...s, name: v, slug: slugTouched ? s.slug : slugify(v) }));
  const setCount = (k: CountKey, v: string) => {
    const n = v === '' ? undefined : Math.max(0, Math.floor(Number(v) || 0));
    setCounts(s => ({ ...s, [k]: n }));
  };

  const cleanedCounts = useMemo<CustomLimits>(() => {
    const out: CustomLimits = {};
    (Object.keys(counts) as CountKey[]).forEach(k => { if (typeof counts[k] === 'number') out[k] = counts[k]; });
    return out;
  }, [counts]);

  const checks = [
    { key: 'name', label: 'Company name', ok: form.name.trim().length >= 2 },
    { key: 'slug', label: 'Code / slug', ok: form.slug.length >= 2 },
    { key: 'industry', label: 'Industry & location', ok: !!form.industry && !!location.country },
    { key: 'plan', label: 'Plan & expiry', ok: !!form.planId && new Date(`${form.expiresAt}T23:59:59.999Z`).getTime() > Date.now() },
    { key: 'trial', label: 'Trial dates', ok: status !== 'trial' || !trialEndsAt || (trialEndsAt <= form.expiresAt && new Date(`${trialEndsAt}T23:59:59.999Z`).getTime() > Date.now()) },
    { key: 'credits', label: 'AI credits', ok: includedAiCredits === '' || (Number.isInteger(Number(includedAiCredits)) && Number(includedAiCredits) >= 0 && Number(includedAiCredits) <= 1_000_000_000) },
    { key: 'admin', label: 'Admin name', ok: !!form.adminFirstName.trim() && !!form.adminLastName.trim() },
    { key: 'email', label: 'Admin email', ok: EMAIL_RE.test(form.adminEmail) },
    { key: 'password', label: 'Password · 8+ characters', ok: form.adminPassword.length >= 8 },
  ];
  const ready = checks.every((c) => c.ok);
  const err = (key: string, msg: string) => (tried && !checks.find((c) => c.key === key)?.ok ? msg : undefined);

  const submit = async () => {
    setTried(true);
    if (!ready) { toast.error('Fill in the highlighted fields'); return; }
    setBusy(true);
    try {
      const c = await companiesService.create({
        ...form,
        status, enabledModules, defaultBotMode,
        includedAiCredits: includedAiCredits === '' ? undefined : Number(includedAiCredits),
        trialEndsAt: status === 'trial' && trialEndsAt ? `${trialEndsAt}T23:59:59.999Z` : undefined,
        logoUrl,
        expiresAt: `${form.expiresAt}T23:59:59.999Z`,
        industry: form.industry || undefined,
        applyBlueprint: chosenBlueprint ? applyBlueprint : undefined,
        ...(location.country ? { country: location.country } : {}),
        ...(location.defaultCurrency ? { defaultCurrency: location.defaultCurrency } : {}),
        ...(location.timezone ? { timezone: location.timezone } : {}),
        usesCustomPricing,
        customLimits: usesCustomPricing ? cleanedCounts : undefined,
      });
      toast.success(`Created ${c.name}`);
      if (c.setupWarnings?.length) toast(c.setupWarnings[0], { duration: 8000 });
      nav(`/companies/${c.companyUid}`);
    } catch { /* axios interceptor toasts */ } finally { setBusy(false); }
  };

  const plan = plans.find((p) => p._id === form.planId);
  const tpl = form.industry ? templates?.[form.industry] : undefined;
  const IndustryIcon = form.industry ? industryIcon(form.industry, industryGroup(form.industry)) : Shapes;
  const initials = (form.name || 'New company').split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

  return (
    <>
      <Topbar title="Add company" breadcrumb="Customers / Companies / New" backTo="/companies" />
      <div className="admin-page grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="space-y-5">
          <Section icon={<Building2 size={17} />} title="Company" subtitle="How the client appears across SlightorCRM." delay={0}>
            <div className="mb-5 flex items-center gap-4">
              <LogoUpload value={logoUrl} name={form.name || 'New company'} onChange={setLogoUrl} size={68} />
              <div className="text-xs text-text-muted">
                <div className="font-semibold text-text-primary">Logo <span className="font-normal text-text-muted">· optional</span></div>
                Square PNG, JPG or SVG up to 5 MB. Without one the client shows its initials.
              </div>
            </div>
            <div className="grid gap-x-4 sm:grid-cols-2">
              <FormField label="Company name" required error={err('name', 'At least 2 characters')}>
                <Input value={form.name} onChange={(e) => setName(e.target.value)} placeholder="Ebkar Restaurant" invalid={!!err('name', 'x')} />
              </FormField>
              <FormField label="Code / slug" required hint="URL identifier" error={err('slug', 'At least 2 characters')}>
                <Input
                  value={form.slug}
                  onChange={(e) => { setSlugTouched(true); set('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')); }}
                  placeholder="ebkar"
                  iconLeft={<AtSign size={14} />}
                  className="font-mono"
                  invalid={!!err('slug', 'x')}
                />
              </FormField>
            </div>
            <FormField label="Website domain" hint="optional">
              <Input value={form.domain} onChange={(e) => set('domain', e.target.value)} placeholder="ebkar.ae" iconLeft={<Globe size={14} />} />
            </FormField>
          </Section>

          <Section icon={<Shapes size={17} />} title="Industry" subtitle="Pick the family, then the type. It shapes the client's CRM from day one." delay={40} raised>
            <div className="mb-4 grid gap-2.5 sm:grid-cols-3">
              {(blueprints ?? []).map((b) => {
                const FIcon = familyIcon(b.familyKey);
                const on = family === b.familyKey;
                return (
                  <button key={b.familyKey} type="button" onClick={() => pickFamily(b.familyKey)} aria-pressed={on}
                    className={clsx('flex flex-col gap-2 rounded-xl border p-3.5 text-left transition-all',
                      on ? 'border-primary bg-primary-soft/60 shadow-soft' : 'border-border hover:border-border-strong')}>
                    <span className="flex items-center gap-2">
                      <span className={clsx('flex h-8 w-8 items-center justify-center rounded-lg', on ? 'bg-primary text-white' : 'bg-primary-soft text-primary')}><FIcon size={16} /></span>
                      <span className="text-sm font-semibold text-text-primary">{b.name}</span>
                    </span>
                    <span className="text-2xs leading-relaxed text-text-muted">{b.description}</span>
                  </button>
                );
              })}
              <button type="button" onClick={() => pickFamily('other')} aria-pressed={family === 'other'}
                className={clsx('flex flex-col gap-2 rounded-xl border p-3.5 text-left transition-all',
                  family === 'other' ? 'border-primary bg-primary-soft/60 shadow-soft' : 'border-border hover:border-border-strong')}>
                <span className="flex items-center gap-2">
                  <span className={clsx('flex h-8 w-8 items-center justify-center rounded-lg', family === 'other' ? 'bg-primary text-white' : 'bg-surface-alt text-text-muted')}><Shapes size={16} /></span>
                  <span className="text-sm font-semibold text-text-primary">Other industries</span>
                </span>
                <span className="text-2xs leading-relaxed text-text-muted">Clinics, salons, retail and the rest — the standard CRM.</span>
              </button>
            </div>

            {chosenBlueprint && (
              <FormField label="Type" required>
                <div className="flex flex-wrap gap-2">
                  {chosenBlueprint.industries.map((k) => {
                    const TIcon = industryIcon(k, industryGroup(k));
                    const on = form.industry === k;
                    return (
                      <button key={k} type="button" onClick={() => set('industry', k)} aria-pressed={on}
                        className={clsx('inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors',
                          on ? 'border-primary bg-primary text-white' : 'border-border text-text-secondary hover:border-border-strong')}>
                        <TIcon size={14} /> {industryLabel(k)}
                      </button>
                    );
                  })}
                </div>
              </FormField>
            )}

            {family === 'other' && (
              <FormField label="Industry" required error={err('industry', 'Select an industry and country')}>
                <IndustryPicker
                  value={form.industry}
                  onChange={(v) => set('industry', v)}
                  templates={templates ?? undefined}
                  exclude={familyIndustries}
                  placeholder="Choose the client's industry"
                />
              </FormField>
            )}

            {form.industry && chosenBlueprint ? (
              <div className="mb-5 space-y-3">
                <div className="flex items-start justify-between gap-4 rounded-xl border border-border bg-bg/60 p-3.5">
                  <div>
                    <div className="text-sm font-semibold text-text-primary">Set up the CRM from the {chosenBlueprint.name} blueprint</div>
                    <p className="mt-0.5 text-xs text-text-muted">Pages and words follow it either way. Switch off only when you'll import an existing setup.</p>
                  </div>
                  <Switch checked={applyBlueprint} onChange={setApplyBlueprint} label="Apply blueprint" />
                </div>
                {applyBlueprint && (bpPlan
                  ? <BlueprintPlanSummary plan={bpPlan} registry={registry} mode="new" />
                  : <div className="skeleton h-40 rounded-xl" />)}
                <p className="flex items-start gap-1.5 text-xs text-text-secondary">
                  <Lock size={13} className="mt-0.5 shrink-0 text-primary" />
                  Onboarding opens with {industryLabel(form.industry)} already set. The client can't change it — only you can, from this panel.
                </p>
              </div>
            ) : form.industry ? (
              <div className="mb-5 grid gap-3 sm:grid-cols-2">
                <div className="rounded-xl border border-border bg-bg/60 p-3.5">
                  <div className="mb-2 text-2xs font-semibold uppercase tracking-[0.08em] text-text-muted">Copied into the company</div>
                  {tpl && tpl.fields > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {ENTITY_ORDER.map((e) => {
                        const Icon = ENTITY_META[e].icon;
                        return (
                          <span key={e} className={clsx(
                            'inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium tabular-nums',
                            tpl.byEntity[e] > 0 ? 'bg-primary-soft text-primary' : 'bg-surface-alt text-text-muted',
                          )}>
                            <Icon size={12} /> {tpl.byEntity[e]} {ENTITY_META[e].label.toLowerCase()}
                          </span>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-text-muted">
                      No default fields for {industryLabel(form.industry)} yet.{' '}
                      <Link to={`/industry-configs/${form.industry}`} className="font-medium text-primary hover:underline">Add them</Link>
                    </p>
                  )}
                </div>
                <div className="rounded-xl border border-border bg-bg/60 p-3.5">
                  <div className="mb-2 text-2xs font-semibold uppercase tracking-[0.08em] text-text-muted">In the client's CRM</div>
                  <p className="flex items-start gap-1.5 text-xs text-text-secondary">
                    <Lock size={13} className="mt-0.5 shrink-0 text-primary" />
                    Standard pages and words. Onboarding opens with {industryLabel(form.industry)} set, locked to this panel.
                  </p>
                </div>
              </div>
            ) : (
              <p className="mb-5 flex items-start gap-1.5 rounded-xl border border-dashed border-border px-3.5 py-3 text-xs text-text-muted">
                <Info size={13} className="mt-0.5 shrink-0" />
                {family && family !== 'other' ? 'Pick the type to preview what the client gets.' : 'Without an industry the client picks one at onboarding. It then shows up here, locked to that pick.'}
              </p>
            )}
          </Section>

          <Section icon={<Globe size={17} />} title="Location & currency" subtitle="Deals, documents and prices start in this currency. The client can't change it — only VAT." delay={60}>
            <LocationFields value={location} onChange={setLocation} options={locOptions} />
          </Section>

          <Section icon={<CreditCard size={17} />} title="Plan & billing" subtitle="What the client pays and until when." delay={80}>
            <div className="grid gap-x-4 sm:grid-cols-2">
              <FormField label="Account status"><Select value={status} onChange={event => setStatus(event.target.value as typeof status)}><option value="active">Active</option><option value="trial">Trial</option><option value="suspended">Suspended</option></Select></FormField>
              {status === 'trial' && <FormField label="Trial ends" hint="blank uses account expiry" error={err('trial', 'Choose a future date no later than expiry')}><Input type="date" value={trialEndsAt} max={form.expiresAt} onChange={event => setTrialEndsAt(event.target.value)} /></FormField>}
            </div>
            <div className="grid gap-x-4 sm:grid-cols-2">
              <FormField label="Plan" required>
                <Select value={form.planId} onChange={(e) => { set('planId', e.target.value); setModulesEdited(false); setEnabledModules(plans.find(plan => plan._id === e.target.value)?.includedModules ?? DEFAULT_MODULE_KEYS); }}>
                  {plans.map(p => <option key={p._id} value={p._id}>{p.displayName} — ${p.monthlyPriceUsd}/mo</option>)}
                </Select>
              </FormField>
              <FormField label="Expires" required>
                <Input type="date" value={form.expiresAt} onChange={(e) => set('expiresAt', e.target.value)} />
              </FormField>
            </div>

            <div className="mb-5 flex items-start justify-between gap-4 rounded-xl border border-border bg-bg/60 p-3.5">
              <div>
                <div className="text-sm font-semibold text-text-primary">Custom per-unit pricing</div>
                <p className="mt-0.5 text-xs text-text-muted">Override the plan's limits and charge per unit from the Pricing Catalog. Blank fields keep the plan default.</p>
              </div>
              <Switch checked={usesCustomPricing} onChange={setUsesCustomPricing} label="Custom per-unit pricing" />
            </div>

            {usesCustomPricing && (
              <div className="grid grid-cols-2 gap-x-4 md:grid-cols-3">
                {COUNT_FIELDS.map(f => (
                  <FormField key={f.key} label={f.label} hint={f.help}>
                    <Input type="number" min={0} value={counts[f.key] ?? ''} onChange={(e) => setCount(f.key, e.target.value)} />
                  </FormField>
                ))}
              </div>
            )}

            {usesCustomPricing && quote && (
              <div className="mb-5 rounded-xl border border-border bg-bg p-4">
                <div className="mb-2 flex justify-between text-xs text-text-muted">
                  <span>Estimated monthly total</span>
                  <span>{quote.currency}</span>
                </div>
                <div className="mb-3 flex justify-between text-sm text-text-primary">
                  <span>Base fee</span>
                  <span className="tabular-nums">{quote.currency} {quote.baseMonthlyFee.toFixed(2)}</span>
                </div>
                {quote.lineItems.length > 0 && (
                  <div className="mb-3 border-t border-border pt-2">
                    {quote.lineItems.map((li, i) => (
                      <div key={i} className="mb-1 flex justify-between text-sm">
                        <span className="text-text-muted">{li.resource} ({li.count} × {li.unit})</span>
                        <span className="tabular-nums text-text-primary">{quote.currency} {li.subtotal.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex justify-between border-t border-border pt-2 font-bold text-text-primary">
                  <span>Total / month</span>
                  <span className="tabular-nums">{quote.currency} {quote.total.toFixed(2)}</span>
                </div>
              </div>
            )}
          </Section>

          <Section icon={<LayoutGrid size={17} />} title="Enabled modules" subtitle="Choose the workspace tools included for this company.">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2 text-xs"><span className="text-text-muted">{enabledModules.length} of {ALL_MODULE_KEYS.length} enabled</span><div className="flex gap-2"><Button size="sm" variant="ghost" onClick={() => setModulesEdited(false)}>Use industry defaults</Button><Button size="sm" variant="ghost" onClick={() => { setModulesEdited(true); setEnabledModules((prev) => [...DEFAULT_MODULE_KEYS, ...prev.filter((k) => MODULE_REGISTRY.some((m) => m.key === k && m.explicit))]); }}>Select all</Button><Button size="sm" variant="ghost" onClick={() => { setModulesEdited(true); setEnabledModules([]); }}>Clear</Button></div></div>
            <div className="mb-5 grid gap-2 sm:grid-cols-2">{MODULE_REGISTRY.map(module => {
              const checked = enabledModules.includes(module.key);
              return <label key={module.key} className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 ${checked ? 'border-primary/40 bg-primary-soft/40' : 'border-border'}`}><input type="checkbox" className="mt-1 accent-primary" checked={checked} onChange={() => { setModulesEdited(true); setEnabledModules(current => checked ? current.filter(key => key !== module.key) : [...current, module.key]); }} /><module.icon size={16} className={`mt-0.5 shrink-0 ${checked ? 'text-primary' : 'text-text-muted'}`} /><span><span className="block text-xs font-semibold">{module.label}</span><span className="mt-1 block text-[11px] leading-relaxed text-text-muted">{module.description}</span></span></label>;
            })}</div>
            {!enabledModules.length && <p className="mb-4 text-xs text-warning">Only account settings will be available until modules are enabled.</p>}
          </Section>

          <Section icon={<Bot size={17} />} title="AI & automation" subtitle="Set the starting credit allowance and reply mode for new WhatsApp numbers.">
            <div className="grid gap-x-4 sm:grid-cols-2"><FormField label="Default reply mode"><Select value={defaultBotMode} onChange={event => setDefaultBotMode(event.target.value as typeof defaultBotMode)}><option value="off">Off — human replies</option><option value="flow">Scripted flow</option><option value="ai_chat">AI conversation</option></Select></FormField><FormField label="Included monthly AI credits" hint="blank uses plan allowance" error={err('credits', 'Enter a whole number from 0 to 1 billion')}><Input type="number" min={0} max={1_000_000_000} step={1} value={includedAiCredits} placeholder={String((usesCustomPricing ? counts.aiCreditsPerMonth : undefined) ?? plan?.limits.aiCreditsPerMonth ?? 0)} onChange={event => setIncludedAiCredits(event.target.value)} /></FormField></div>
            <p className="mb-5 text-xs text-text-muted">The initial allowance is added once when the company is created. New numbers inherit this reply mode.</p>
          </Section>

          <Section icon={<UserRound size={17} />} title="First company admin" subtitle="Signs in to the CRM with this email and finishes onboarding." delay={120}>
            <div className="grid gap-x-4 sm:grid-cols-2">
              <FormField label="First name" required error={err('admin', 'Required')}>
                <Input value={form.adminFirstName} onChange={(e) => set('adminFirstName', e.target.value)} invalid={tried && !form.adminFirstName.trim()} />
              </FormField>
              <FormField label="Last name" required>
                <Input value={form.adminLastName} onChange={(e) => set('adminLastName', e.target.value)} invalid={tried && !form.adminLastName.trim()} />
              </FormField>
            </div>
            <FormField label="Email" required error={err('email', 'Enter a valid email')}>
              <Input type="email" value={form.adminEmail} onChange={(e) => set('adminEmail', e.target.value)} iconLeft={<Mail size={14} />} placeholder="owner@company.com" invalid={!!err('email', 'x')} />
            </FormField>
            <FormField label="Initial password" required hint="share it securely" error={err('password', 'At least 8 characters')}>
              <Input
                type={showPassword ? 'text' : 'password'}
                value={form.adminPassword}
                onChange={(e) => set('adminPassword', e.target.value)}
                iconLeft={<KeyRound size={14} />}
                autoComplete="new-password"
                invalid={!!err('password', 'x')}
                iconRight={(
                  <button type="button" onClick={() => setShowPassword((v) => !v)} className="rounded-md p-1.5 hover:bg-surface-alt hover:text-text-primary" aria-label={showPassword ? 'Hide password' : 'Show password'}>
                    {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                )}
              />
            </FormField>
          </Section>
        </div>

        {/* ── Summary rail ──────────────────────────────────────────────── */}
        <aside className="lg:sticky lg:top-6 lg:self-start">
          <div className="fade-up overflow-hidden rounded-2xl border border-border bg-surface shadow-soft" style={{ animationDelay: '60ms' }}>
            <div className="h-1 bg-gradient-to-r from-primary via-primary/40 to-transparent" />
            <div className="flex items-center gap-3 px-5 pb-4 pt-5">
              {logoUrl ? (
                <img src={logoUrl} alt="" className="h-12 w-12 rounded-xl border border-border object-cover" />
              ) : (
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-soft text-sm font-bold text-primary">{initials}</span>
              )}
              <div className="min-w-0">
                <div className="truncate font-display text-[15px] font-semibold text-text-primary">{form.name || 'New company'}</div>
                <div className="truncate font-mono text-2xs uppercase tracking-wide text-text-muted">{form.slug || 'slug'}</div>
              </div>
            </div>
            <div className="space-y-2.5 border-t border-border px-5 py-4 text-xs">
              <div className="flex items-center justify-between gap-3">
                <span className="text-text-muted">Industry</span>
                <span className="inline-flex min-w-0 items-center gap-1.5 font-medium text-text-primary">
                  <IndustryIcon size={13} className={form.industry ? 'text-primary' : 'text-text-muted'} />
                  <span className="truncate">{industryLabel(form.industry) ?? 'Picked at onboarding'}{chosenBlueprint && form.industry ? ` · ${chosenBlueprint.name}` : ''}</span>
                </span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-text-muted">Currency</span>
                <span className="font-medium text-text-primary">{resolvedLocation(location, locOptions).currency}{location.country ? ` · ${resolvedLocation(location, locOptions).countryLabel}` : ''}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-text-muted">Plan</span>
                <span className="font-medium text-text-primary">{usesCustomPricing ? 'Custom pricing' : plan?.displayName ?? '—'}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-text-muted">Expires</span>
                <span className="font-medium tabular-nums text-text-primary">
                  {form.expiresAt ? new Date(form.expiresAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                </span>
              </div>
              <div className="flex justify-between gap-3"><span className="text-text-muted">Modules</span><span>{enabledModules.length} enabled</span></div>
              <div className="flex justify-between gap-3"><span className="text-text-muted">Account status</span><span className="capitalize">{status}</span></div>
              <div className="flex justify-between gap-3"><span className="text-text-muted">Initial AI credits</span><span>{Number(includedAiCredits === '' ? ((usesCustomPricing ? counts.aiCreditsPerMonth : undefined) ?? plan?.limits.aiCreditsPerMonth ?? 0) : includedAiCredits).toLocaleString()}</span></div>
            </div>
            <ul className="space-y-1.5 border-t border-border px-5 py-4">
              {checks.map((c) => (
                <li key={c.key} className="flex items-center gap-2 text-xs">
                  <span className={clsx(
                    'flex h-4 w-4 shrink-0 items-center justify-center rounded-full',
                    c.ok ? 'bg-success text-white' : tried ? 'bg-danger-soft text-danger' : 'border border-border-strong',
                  )}>
                    {c.ok && <Check size={10} strokeWidth={3} />}
                  </span>
                  <span className={c.ok ? 'text-text-secondary' : tried ? 'text-danger' : 'text-text-muted'}>{c.label}</span>
                </li>
              ))}
            </ul>
            <div className="space-y-2 border-t border-border bg-bg/40 px-5 py-4">
              <Button className="w-full whitespace-nowrap" loading={busy} onClick={submit}>Create company</Button>
              <Button variant="ghost" className="w-full" onClick={() => nav('/companies')}>Cancel</Button>
            </div>
          </div>
        </aside>
      </div>
    </>
  );
}
